import { FC, ReactNode, useContext, useEffect, useReducer, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { Box, Button, Divider, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import { FormattedMessage } from "react-intl";

import FlyInDrawer from "../../../FlyInDrawer/FlyInDrawer";
import { success } from "../../../../utils/AppUtils";
import { GenericProGridContext } from "../../GenericProGridContext/GenericProGridContext";
import { GenericGridConfigurationContext } from "../../../../contexts/GenericGridConfigurationContext/GenericGridConfigurationContext";
import { DialogActions } from "./ViewSelectorTypes";
import { setNameDialogReducer } from "./ViewSelectorReducers";
import ConfirmDialog, { initialConfirmOptions } from "../../../ConfirmDialog/ConfirmDialog";
import SetNameDialog from "./components/SetNameDialog";
import ViewListFlyin from "./components/ViewListFlyin";
import { useTooltipText } from "../../../../Hooks/useTooltipText";

const baseConfirmOptions = {
    ...initialConfirmOptions,
    handleConfirm: () => {
        // This is intentional
    },
};

const ViewSelector: FC = () => {
    const { configManager } = useContext(GenericGridConfigurationContext);
    const {
        identifier: gridIdentifier,
        activeGridConfigMetadata,
        setActiveGridConfigMetadata,
        activeGridState,
        setIncomingGridState,
        defaultGridState,
    } = useContext(GenericProGridContext);

    const theme = useTheme();

    const [open, setOpen] = useState<boolean>(false);
    const [confirmOptions, setConfirmOptions] = useState<{ isShown: boolean; text: ReactNode; title: ReactNode; handleConfirm: () => void }>(
        baseConfirmOptions,
    );

    const [nameDialogState, dispatchSetNameDialogState] = useReducer(setNameDialogReducer, {
        initData: undefined,
        isOpenDialog: false,
        onCancel: () => {
            // This is intentional
        },
        onClose: () => {
            // This is intentional
        },
        onSubmit: () => Promise.resolve(),
        title: "",
    });

    const deselectConfigHandler = () => {
        setActiveGridConfigMetadata(null);
        setIncomingGridState(defaultGridState);
    };

    useEffect(() => {
        if (activeGridConfigMetadata) {
            configManager?.getSpecificGridConfig(gridIdentifier, activeGridConfigMetadata.id).then((res) => {
                setIncomingGridState(res.configuration);
            });
        }
    }, [activeGridConfigMetadata]);

    const ref = useRef<HTMLHeadingElement>(null);
    const tooltipText = useTooltipText(ref, activeGridConfigMetadata?.name);

    const handleCleateBlankView = () => {
        dispatchSetNameDialogState({
            type: DialogActions.CONFIGURE_FORM,
            payload: {
                title: <FormattedMessage id="generic-pro-grid.set-name-dialog.title.name-this-view" defaultMessage="Please input a name for this view" />,
                isOpenDialog: true,
                onClose: () => dispatchSetNameDialogState({ type: DialogActions.RESET, payload: undefined }),
                onCancel: () => dispatchSetNameDialogState({ type: DialogActions.RESET, payload: undefined }),
                onSubmit: (data) =>
                    configManager
                        ? configManager
                              .saveGridConfig(gridIdentifier, {
                                  id: -42,
                                  name: data.name,
                                  isGlobal: false,
                                  configuration: activeGridState,
                                  updated: new Date(),
                              })
                              .then((res) => {
                                  setOpen(false);
                                  setActiveGridConfigMetadata({
                                      id: res.id,
                                      name: res.name,
                                      isGlobal: res.isGlobal,
                                  });
                                  setIncomingGridState(res.configuration);
                                  dispatchSetNameDialogState({ type: DialogActions.RESET, payload: undefined });
                              })
                        : Promise.resolve(),
            },
        });
    };

    return (
        <>
            <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "nowrap", alignItems: "center", minWidth: 0, marginRight: "auto" }}>
                <Button id="long-button" onClick={() => setOpen(true)} sx={{ cursor: "pointer" }}>
                    <Typography
                        variant="body1"
                        sx={{
                            color: "inherit",
                        }}
                    >
                        <FormattedMessage id="grid.actions.button.view" defaultMessage="Manage Views" />
                    </Typography>
                </Button>

                <Divider orientation="vertical" flexItem />

                {/* {activeGridConfigMetadata ? (
                    <Button
                        onClick={() => {
                            configManager
                                ?.saveGridConfig(gridIdentifier, {
                                    id: activeGridConfigMetadata.id,
                                    name: activeGridConfigMetadata.name,
                                    isGlobal: activeGridConfigMetadata.isGlobal,
                                    configuration: activeGridState,
                                    updated: new Date(),
                                })
                                .then((res) => {
                                    setActiveGridConfigMetadata({ id: res.id, name: res.name });
                                    success(<FormattedMessage id="generic-pro-grid.set-name-dialog.message.updated-view" defaultMessage="Updated View" />);
                                });
                        }}
                        disabled={activeGridConfigMetadata.isGlobal}
                    >
                        <Typography color={"inherit"} variant="body1">
                            <FormattedMessage id="generic-pro-grid.view-selector.tooltip.save" defaultMessage="Save View" />
                        </Typography>
                    </Button>
                ) : // <SvgIcon component={ViewsIcon} inheritViewBox sx={{ color: "inherit", padding: "8px" }} />
                null} */}

                {activeGridConfigMetadata ? (
                    <Tooltip title={tooltipText} placement="top" arrow>
                        <Typography
                            ref={ref}
                            color={theme.palette.text.primary}
                            variant="body1"
                            sx={{ padding: "0 8px", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}
                        >
                            {activeGridConfigMetadata?.name}
                        </Typography>
                    </Tooltip>
                ) : (
                    <Button
                        sx={{ color: (muiTheme) => muiTheme.palette.teal?.main, marginRight: "8px" }}
                        onClick={() =>
                            dispatchSetNameDialogState({
                                type: DialogActions.CONFIGURE_FORM,
                                payload: {
                                    title: (
                                        <FormattedMessage
                                            id="generic-pro-grid.set-name-dialog.title.name-this-view"
                                            defaultMessage="Please input a name for this view"
                                        />
                                    ),
                                    isOpenDialog: true,
                                    onClose: () => dispatchSetNameDialogState({ type: DialogActions.RESET, payload: undefined }),
                                    onCancel: () => dispatchSetNameDialogState({ type: DialogActions.RESET, payload: undefined }),
                                    onSubmit: (data) =>
                                        configManager
                                            ? configManager
                                                  .saveGridConfig(gridIdentifier, {
                                                      id: -42,
                                                      name: data.name,
                                                      isGlobal: false,
                                                      configuration: activeGridState,
                                                      updated: new Date(),
                                                  })
                                                  .then((res) => {
                                                      setActiveGridConfigMetadata({
                                                          id: res.id,
                                                          name: res.name,
                                                      });
                                                      dispatchSetNameDialogState({ type: DialogActions.RESET, payload: undefined });
                                                  })
                                            : Promise.resolve(),
                                },
                            })
                        }
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                color: "inherit",
                            }}
                        >
                            <FormattedMessage id="generic-pro-grid.label.save-view-as" defaultMessage="Save Current View" />
                        </Typography>
                    </Button>
                )}

                {activeGridConfigMetadata ? (
                    <>
                        <Tooltip title={<FormattedMessage id="generic-pro-grid.view-selector.tooltip.save" defaultMessage="Save View" />} placement="top" arrow>
                            <span>
                                <IconButton
                                    aria-label="Save View"
                                    onClick={() => {
                                        configManager
                                            ?.saveGridConfig(gridIdentifier, {
                                                id: activeGridConfigMetadata.id,
                                                name: activeGridConfigMetadata.name,
                                                isGlobal: activeGridConfigMetadata.isGlobal,
                                                configuration: activeGridState,
                                                updated: new Date(),
                                            })
                                            .then((res) => {
                                                setActiveGridConfigMetadata({ id: res.id, name: res.name });
                                                success(
                                                    <FormattedMessage
                                                        id="generic-pro-grid.set-name-dialog.message.updated-view"
                                                        defaultMessage="Updated View"
                                                    />,
                                                );
                                            });
                                    }}
                                    disabled={activeGridConfigMetadata.isGlobal}
                                >
                                    <SaveIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip
                            title={<FormattedMessage id="generic-pro-grid.view-selector.tooltip.close" defaultMessage="Close View" />}
                            placement="top"
                            arrow
                        >
                            <IconButton aria-label="Close View" onClick={deselectConfigHandler}>
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </>
                ) : null}
            </Box>
            <SetNameDialog
                dialogTitle={nameDialogState.title}
                open={nameDialogState.isOpenDialog}
                onClose={nameDialogState.onClose}
                onCancel={nameDialogState.onCancel}
                onSubmit={nameDialogState.onSubmit}
                initData={nameDialogState.initData}
            />
            <ConfirmDialog
                confirmOptions={{
                    isShown: confirmOptions.isShown,
                    text: confirmOptions.text,
                    title: confirmOptions.title,
                }}
                handleAccept={() => {
                    confirmOptions.handleConfirm();
                    setConfirmOptions(baseConfirmOptions);
                }}
                handleCancel={() => setConfirmOptions(baseConfirmOptions)}
                yesText={<FormattedMessage id="common.button-label.confirm" defaultMessage="Confirm" />}
                noText={<FormattedMessage id="common.button-label.cancel" defaultMessage="Cancel" />}
            />
            <FlyInDrawer isOpen={open} onClose={() => setOpen(false)} closeDrawer={() => setOpen(false)}>
                <ViewListFlyin
                    closeDrawer={() => setOpen(false)}
                    editHandler={(config) => {
                        dispatchSetNameDialogState({
                            type: DialogActions.CONFIGURE_FORM,
                            payload: {
                                isOpenDialog: true,
                                title: <FormattedMessage id="generic-pro-grid.set-name-dialog.title.rename-this-view" defaultMessage="Rename This View" />,
                                initData: { name: config.name },
                                onClose: () =>
                                    dispatchSetNameDialogState({
                                        type: DialogActions.RESET,
                                        payload: undefined,
                                    }),
                                onCancel: () =>
                                    dispatchSetNameDialogState({
                                        type: DialogActions.RESET,
                                        payload: undefined,
                                    }),
                                onSubmit: (data) =>
                                    configManager
                                        ? configManager
                                              .saveGridConfig(gridIdentifier, {
                                                  id: config.id,
                                                  name: data.name,
                                                  isGlobal: config.isGlobal,
                                                  configuration: config.configuration,
                                                  updated: new Date(),
                                              })
                                              .then((res) => {
                                                  setActiveGridConfigMetadata({
                                                      id: res.id,
                                                      name: res.name,
                                                      isGlobal: res.isGlobal,
                                                  });
                                                  success(
                                                      <FormattedMessage
                                                          id="generic-pro-grid.set-name-dialog.message.updated-config-name"
                                                          defaultMessage="Updated config"
                                                      />,
                                                  );
                                                  dispatchSetNameDialogState({
                                                      type: DialogActions.RESET,
                                                      payload: undefined,
                                                  });
                                                  setOpen(false);
                                              })
                                        : Promise.resolve(),
                            },
                        });
                    }}
                    copyHandler={(config) => {
                        dispatchSetNameDialogState({
                            type: DialogActions.CONFIGURE_FORM,
                            payload: {
                                isOpenDialog: true,
                                title: <FormattedMessage id="generic-pro-grid.set-name-dialog.title.copy-this-view" defaultMessage="Copy This View" />,
                                onClose: () =>
                                    dispatchSetNameDialogState({
                                        type: DialogActions.RESET,
                                        payload: undefined,
                                    }),
                                onCancel: () =>
                                    dispatchSetNameDialogState({
                                        type: DialogActions.RESET,
                                        payload: undefined,
                                    }),
                                onSubmit: (data) =>
                                    configManager
                                        ? configManager
                                              .saveGridConfig(gridIdentifier, {
                                                  id: -42,
                                                  name: data.name,
                                                  isGlobal: false,
                                                  configuration: config.configuration,
                                                  updated: new Date(),
                                              })
                                              .then((res) => {
                                                  setActiveGridConfigMetadata({
                                                      id: res.id,
                                                      name: res.name,
                                                      isGlobal: res.isGlobal,
                                                  });

                                                  // here it is the previous value !
                                                  if (activeGridConfigMetadata?.id !== config?.id && config?.id && activeGridConfigMetadata?.id) {
                                                      setIncomingGridState(res.configuration);
                                                  }

                                                  success(
                                                      <FormattedMessage
                                                          id="generic-pro-grid.set-name-dialog.message.created-view"
                                                          defaultMessage="Created View"
                                                      />,
                                                  );
                                                  dispatchSetNameDialogState({
                                                      type: DialogActions.RESET,
                                                      payload: undefined,
                                                  });
                                                  setOpen(false);
                                              })
                                        : Promise.resolve(),
                            },
                        });
                    }}
                    deleteHandler={(config, onDeleteComplete) => {
                        setConfirmOptions({
                            isShown: true,
                            text: (
                                <FormattedMessage
                                    id="generic-pro-grid.confirm-delete-dialog.message.text"
                                    defaultMessage="Are you sure you want to delete {name} view?"
                                    values={{ name: config.name }}
                                />
                            ),
                            title: <FormattedMessage id="generic-pro-grid.title.confirm-delete-dialog" defaultMessage="Please Confirm" />,
                            handleConfirm: () =>
                                configManager?.deleteGridConfig(gridIdentifier, config.id).then(() => {
                                    onDeleteComplete();
                                    success(<FormattedMessage id="generic-pro-grid.set-name-dialog.message.deleted-view" defaultMessage="Deleted View" />);
                                    deselectConfigHandler();
                                }),
                        });
                    }}
                    createBlankView={handleCleateBlankView}
                />
            </FlyInDrawer>
        </>
    );
};

export default ViewSelector;
